import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { init, isTMA } from '@telegram-apps/sdk-react';
import { routeTree } from './routeTree.gen';
// Шрифты — в бандл через @fontsource (не зависим от Google CDN: критично для клиентов
// в регионах с блокировками, где fonts.googleapis.com может не ответить). Как в
// partner-webapp. Веса ровно те, что раньше тянулись из Google.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';
import './index.css';
import './i18n';
import { getThemeParams, onThemeChanged, requestFullscreen, applyBrandPrimary } from '@/lib/telegram';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/query-client';
import { api, currentBotId } from '@/lib/api';

// Sentry lazy-load: SDK ~50KB. Загружаем только если DSN сконфигурен и после
// первого render'а — start-bundle меньше, time-to-interactive быстрее.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.VITE_SENTRY_ENV || 'dev',
      release: import.meta.env.VITE_APP_VERSION as string | undefined,
      tracesSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.0,
      replaysSessionSampleRate: 0.0,
    });
  });
}

// Block E.1: инициализация @telegram-apps/sdk-react. Без `init()` все обёртки
// `*.isAvailable()` падают в FunctionNotAvailableError → helpers упадут на fallback.
// В non-TMA окружении (просто открыли в браузере) `init()` бросает — гасим, всё работает
// на mock-данных и raw WebApp shim.
try {
  if (isTMA()) {
    init();
  }
} catch (e) {
  console.warn('[telegram-sdk] init skipped:', e);
}

// Кнопка рассылки открыла мини-апп: в адресе метка `bc` (рассылка) и `bb` (кнопка).
// Снимаем её сразу, пока роутер не потерял search-параметры при первом переходе, и
// отправляем (`request` сам дождётся входа): партнёр видит нажатия в «Результате
// рассылки» (заявка #203).
{
  const params = new URLSearchParams(window.location.search);
  const bc = params.get('bc');
  const bb = Number(params.get('bb'));
  if (bc && /^[0-9a-f-]{36}$/i.test(bc) && Number.isInteger(bb) && bb >= 0) {
    void api.reportBroadcastClick(bc, bb).catch(() => undefined);
  }
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 50,
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const setTgThemeVariables = () => {
  const theme = getThemeParams();
  if (!theme.bg_color) return;
  const root = document.documentElement;
  const set = (key: string, value: string | undefined) => {
    if (value) root.style.setProperty(key, value);
  };
  set('--tg-theme-bg-color', theme.bg_color);
  set('--tg-theme-text-color', theme.text_color);
  set('--tg-theme-hint-color', theme.hint_color);
  set('--tg-theme-link-color', theme.link_color);
  set('--tg-theme-button-color', theme.button_color);
  set('--tg-theme-button-text-color', theme.button_text_color);
  set('--tg-theme-secondary-bg-color', theme.secondary_bg_color);
};

setTgThemeVariables();

// Применить закешированный основной цвет ДО auth — чтобы не мигало голубым на
// первом кадре. Свежее значение придёт из auth response и перезапишет это.
// Ключ обязательно с bot_id: без него цвет одного бота доставался всем остальным,
// и сбросить его было нечем (auth бота без цвета тему не трогал).
try {
  const botId = currentBotId();
  const cachedPrimary = botId ? localStorage.getItem(`brand_primary:${botId}`) : null;
  if (cachedPrimary) applyBrandPrimary(cachedPrimary);
} catch {
  // ignore
}

// Block B.8: реакция на смену темы в Telegram (юзер переключил dark↔light)
onThemeChanged(setTgThemeVariables);

// Block E.3: fullscreen mode (Bot API 8.0+). На старых клиентах — no-op.
requestFullscreen();

// Eager parallel prefetch: запросы стартуют сразу после auth, к mount'у /me
// и /profile они уже в кэше. Auth внутри tryRequest memoize'д — fetch один раз.
queryClient
  .prefetchQuery({ queryKey: ['subs'], queryFn: () => api.allSubscriptions() })
  .catch(() => {});
queryClient
  .prefetchQuery({ queryKey: ['profile'], queryFn: () => api.profile() })
  .catch(() => {});
queryClient
  .prefetchQuery({ queryKey: ['client-info'], queryFn: () => api.info() })
  .catch(() => {});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>,
);
